import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

// Custom hook for managing virtualized member list state and optimizations
export function useVirtualizedList(members, dependencies = [], options = {}) {
    const {
        externalSortBy,
        onSortChange,
        sortOptions = [],
        externalViewMode,
        onViewModeChange
    } = options;

    const [internalSortBy, setInternalSortBy] = useState('joined');
    const [internalViewMode, setInternalViewMode] = useState('cards');
    const [pinnedMemberId, setPinnedMemberId] = useState(null);
    const listRef = useRef(null);
    const sortedMembersRef = useRef([]);

    const sortBy = externalSortBy ?? internalSortBy;
    const viewMode = externalViewMode ?? internalViewMode;

    const resolvedSortOptions = useMemo(() => {
        if (Array.isArray(sortOptions) && sortOptions.length > 0) {
            return sortOptions;
        }

        return [
            { value: 'joined', label: 'Recently Joined', builtin: true, type: 'date' },
            { value: 'joined_asc', label: 'Oldest Joined', builtin: true, type: 'date' },
            { value: 'name', label: 'Name (A-Z)', builtin: true, type: 'text' },
            { value: 'name_desc', label: 'Name (Z-A)', builtin: true, type: 'text' }
        ];
    }, [sortOptions]);

    const sortOptionMap = useMemo(() => {
        const map = new Map();
        resolvedSortOptions.forEach((option) => {
            if (option?.value) {
                map.set(option.value, option);
            }
        });
        return map;
    }, [resolvedSortOptions]);

    const normalizeText = useCallback((value) => {
        if (Array.isArray(value)) {
            return value.filter((entry) => entry !== null && entry !== undefined).join(', ').toLowerCase();
        }

        if (value === null || value === undefined) {
            return '';
        }

        return String(value).trim().toLowerCase();
    }, []);

    const normalizeNumber = useCallback((value) => {
        if (Array.isArray(value)) {
            value = value.find((entry) => entry !== null && entry !== undefined && String(entry).trim() !== '');
        }

        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }, []);

    const normalizeDate = useCallback((value) => {
        if (Array.isArray(value)) {
            value = value.find((entry) => entry !== null && entry !== undefined && String(entry).trim() !== '');
        }

        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : null;
    }, []);

    const compareWithEmptyLast = useCallback((a, b, direction, comparator) => {
        const aEmpty = a === null || a === undefined || a === '';
        const bEmpty = b === null || b === undefined || b === '';

        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;

        return direction === 'desc' ? comparator(b, a) : comparator(a, b);
    }, []);

    const getMemberFieldValue = useCallback((member, fieldId) => {
        if (!member?.display_fields || !Array.isArray(member.display_fields)) {
            return null;
        }

        const target = member.display_fields.find((field) => Number(field.field_id) === Number(fieldId));
        if (!target) {
            return null;
        }

        return target.sort_value ?? target.value ?? null;
    }, []);

    const compareMembers = useCallback((a, b, currentSortBy) => {
        switch (currentSortBy) {
            case 'name':
                return compareWithEmptyLast(
                    normalizeText(a.display_name),
                    normalizeText(b.display_name),
                    'asc',
                    (left, right) => left.localeCompare(right)
                );
            case 'name_desc':
                return compareWithEmptyLast(
                    normalizeText(a.display_name),
                    normalizeText(b.display_name),
                    'desc',
                    (left, right) => left.localeCompare(right)
                );
            case 'joined':
                return compareWithEmptyLast(
                    normalizeDate(a.member_since),
                    normalizeDate(b.member_since),
                    'desc',
                    (left, right) => left - right
                );
            case 'joined_asc':
                return compareWithEmptyLast(
                    normalizeDate(a.member_since),
                    normalizeDate(b.member_since),
                    'asc',
                    (left, right) => left - right
                );
            default:
                break;
        }

        const option = sortOptionMap.get(currentSortBy);
        if (!option || option.fieldId === undefined || option.fieldId === null) {
            return 0;
        }

        const direction = option.direction === 'desc' ? 'desc' : 'asc';
        const rawA = getMemberFieldValue(a, option.fieldId);
        const rawB = getMemberFieldValue(b, option.fieldId);

        if (option.type === 'number') {
            return compareWithEmptyLast(
                normalizeNumber(rawA),
                normalizeNumber(rawB),
                direction,
                (left, right) => left - right
            );
        }

        if (option.type === 'date') {
            return compareWithEmptyLast(
                normalizeDate(rawA),
                normalizeDate(rawB),
                direction,
                (left, right) => left - right
            );
        }

        return compareWithEmptyLast(
            normalizeText(rawA),
            normalizeText(rawB),
            direction,
            (left, right) => left.localeCompare(right)
        );
    }, [compareWithEmptyLast, getMemberFieldValue, normalizeDate, normalizeNumber, normalizeText, sortOptionMap]);

    useEffect(() => {
        if (!sortOptionMap.has(sortBy)) {
            if (onSortChange) {
                onSortChange('joined');
            } else {
                setInternalSortBy('joined');
            }
        }
    }, [onSortChange, sortBy, sortOptionMap]);

    // Debounced sorting to prevent excessive re-computations
    const sortedMembers = useMemo(() => {
        if (!members || members.length === 0) return [];

        const sorted = [...members].sort((a, b) => compareMembers(a, b, sortBy));

        // Move pinned member to position 0 if set
        if (pinnedMemberId) {
            const pinnedIndex = sorted.findIndex(m => m.user_id === pinnedMemberId);
            if (pinnedIndex > 0) {
                const [pinned] = sorted.splice(pinnedIndex, 1);
                sorted.unshift(pinned);
            }
        }

        // Cache sorted results for performance
        sortedMembersRef.current = sorted;
        return sorted;
    }, [members, sortBy, pinnedMemberId, compareMembers, ...dependencies]);

    // Pin a member to the top of the list
    const pinMember = useCallback((memberId) => {
        setPinnedMemberId(memberId);
    }, []);

    // Clear the pinned member
    const clearPinnedMember = useCallback(() => {
        setPinnedMemberId(null);
    }, []);

    // Optimized scroll to member function
    const scrollToMember = useCallback((memberId) => {
        const memberIndex = sortedMembersRef.current.findIndex(m => m.user_id === memberId);
        if (memberIndex !== -1 && listRef.current) {
            listRef.current.scrollToItem(memberIndex, 'center');
            return true;
        }
        return false;
    }, []);

    // Batch view mode changes to prevent multiple re-renders
    const changeViewMode = useCallback((newMode) => {
        if (newMode !== viewMode) {
            if (onViewModeChange) {
                onViewModeChange(newMode);
            } else {
                setInternalViewMode(newMode);
            }

            // Small delay to allow component to re-render before resetting scroll position
            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.resetAfterIndex(0);
                }
            }, 0);
        }
    }, [viewMode, onViewModeChange]);

    // Optimized sort change with immediate feedback
    const changeSortBy = useCallback((newSort) => {
        if (newSort !== sortBy) {
            if (onSortChange) {
                onSortChange(newSort);
            } else {
                setInternalSortBy(newSort);
            }
        }
    }, [sortBy, onSortChange]);

    // Get visible range for debugging/optimization
    const getVisibleRange = useCallback(() => {
        if (!listRef.current) return null;
        
        // This would need to be implemented based on react-window's internals
        // For now, return basic info
        return {
            startIndex: 0,
            endIndex: Math.min(10, sortedMembers.length - 1)
        };
    }, [sortedMembers.length]);

    return {
        sortBy,
        viewMode,
        sortedMembers,
        listRef,
        pinnedMemberId,
        setSortBy: changeSortBy,
        setViewMode: changeViewMode,
        scrollToMember,
        pinMember,
        clearPinnedMember,
        getVisibleRange
    };
}

// Custom hook for managing hover states with debouncing
export function useOptimizedHover(onMemberHover, onMemberHoverOut, delay = 100) {
    const hoverTimeoutRef = useRef(null);
    const currentHoveredRef = useRef(null);

    const handleMemberHover = useCallback((member) => {
        // Clear any existing timeout
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        // Don't trigger if it's the same member
        if (currentHoveredRef.current?.user_id === member.user_id) {
            return;
        }

        // Set timeout for hover
        hoverTimeoutRef.current = setTimeout(() => {
            currentHoveredRef.current = member;
            onMemberHover?.(member);
        }, delay);
    }, [onMemberHover, delay]);

    const handleMemberHoverOut = useCallback(() => {
        // Clear any pending hover
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        // Small delay before clearing hover to prevent flickering
        setTimeout(() => {
            currentHoveredRef.current = null;
            onMemberHoverOut?.();
        }, 50);
    }, [onMemberHoverOut]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    return {
        handleMemberHover,
        handleMemberHoverOut
    };
}

// Custom hook for managing favorites state efficiently
export function useOptimizedFavorites(isFavorite, isPendingFavorite, onToggleFavorite) {
    const favoritesCache = useRef(new Map());
    const pendingCache = useRef(new Map());

    // Memoized favorite checker
    const checkIsFavorite = useCallback((userId) => {
        if (favoritesCache.current.has(userId)) {
            return favoritesCache.current.get(userId);
        }
        const result = isFavorite(userId);
        favoritesCache.current.set(userId, result);
        return result;
    }, [isFavorite]);

    // Memoized pending checker
    const checkIsPending = useCallback((userId) => {
        if (pendingCache.current.has(userId)) {
            return pendingCache.current.get(userId);
        }
        const result = isPendingFavorite(userId);
        pendingCache.current.set(userId, result);
        return result;
    }, [isPendingFavorite]);

    // Optimized toggle with cache invalidation
    const handleToggleFavorite = useCallback((userId) => {
        // Invalidate cache for this user
        favoritesCache.current.delete(userId);
        pendingCache.current.delete(userId);
        
        // Trigger the actual toggle
        onToggleFavorite(userId);
    }, [onToggleFavorite]);

    // Clear cache when dependencies change
    useEffect(() => {
        favoritesCache.current.clear();
        pendingCache.current.clear();
    }, [isFavorite, isPendingFavorite]);

    return {
        checkIsFavorite,
        checkIsPending,
        handleToggleFavorite
    };
}
